/**
 * Reads supported feature rollout flags from localStorage (`featureSupportAdminCheck`),
 * populated by GET_SUPPORTED_FEATURE_LIST via CloudRestApiAccessProvider.
 * Distinct from RBAC — combine with roleManagement.checkUser at call sites when needed.
 */
import { useSyncExternalStore } from 'react';

export const FEATURE_FLAGS_UPDATED_EVENT = 'featureFlagsUpdated';
const FEATURE_FLAGS_EVENT = FEATURE_FLAGS_UPDATED_EVENT;

/** Backend-supported feature IDs (string). Extend when product confirms new IDs. */
export const FEATURE_IDS = {
  PROTECTED_PORT: '234',
  INSTANT_WIFI_DEBUG_LOG: '201',
  MOBILE_PROFILE_GPS: '206',
  NETWORK_SETTINGS_HARDWARE_TOGGLE: '225',
  NETWORK_SETTINGS_PROBING_TOGGLE: '233',
  LOAD_BALANCING_STICKY_CLIENT: '248',
  CLOUD_REST_API: '259',
  ICM_TX_POWER_ENHANCEMENT: '295',
  // PRJCBUGEN-66599: "Channel Change on WiFi Congestion" toggle on ICM locations.
  ICM_OBSS_TRIGGER: '317',
  BELL_CRITICAL_ONLY: '297',
  DHCP_SNOOPING_RELAY: '298',
  REPORTS: '299',
  PRX_ADVANCED_FEATURES: '302',
  CLIENT_OS_BLOCK_POLICY: '304',
  // PRJCBUGEN-66106: gates device API migration to the new appcom (connected-clients list first). Opt-in: absent or '0' keeps the legacy call.
  APPCOM_DEVICE_APIS_MIGRATION_ENABLED_WEB: 'APPCOM_DEVICE_APIS_MIGRATION_ENABLED_WEB',
  ORBI_DEVICE_SUPPORT: '305',
  MAPS: 'MAPS',
  /** MHS device → Settings → WiFi Networks table inline edit (Security, Channel, Channel Width). */
  MHS_WIFI_NETWORKS_EDIT: '314',
  WIRED_MANAGEMENT_VLAN: '308',
  // Captive Portal (ICP) reports. Gates the "Captive Portal" report type in the
  // Add/Edit Report wizard. Backend-driven via InsightFeatureAccess — hidden
  // until the env's feature list returns code 'CAPTIVE_PORTAL_REPORTING' with status '1'.
  CAPTIVE_PORTAL_REPORTS: 'CAPTIVE_PORTAL_REPORTING',
  ANTENNA_SWITCHING_WBE70XY: '312',
  REBOOT_SCHEDULE: 'REBOOT_SCHEDULE',
  // Switch Stacking display (PRJCBUGEN-63800): gates the Devices-table stack grouping +
  // the Stack Detail page. Backend-driven via the supported-feature list.
  SWITCH_STACKING: 'SWITCH_STACKING',
  // Schema-driven create-stack write path (PRJCBUGEN-64186): when ON, the Create-Stack wizard
  // fetches a config-schema bundle, builds the SCHEMA_CLI messages with the generic engine and
  // posts them to DevCom, instead of relying on AppCom's createStack to message the switches.
  // Read as DEFAULT-ON via useFeatureEnabledByDefault — enabled unless the backend explicitly
  // returns status '0' (a kill-switch). LOAD-BEARING: this path expects an AppCom that no longer
  // messages the device on createStack (the read-only refactor); it ships together with it, so an
  // environment still running an AppCom that messages the device must disable this flag to avoid
  // configuring the switches twice.
  SWITCH_STACKING_SCHEMA_CONFIG: 'SWITCH_STACKING_SCHEMA_CONFIG',
  /** Gearbox AV remote tunnel launch (GB406). Backend-driven via featureConf rules. */
  GEARBOX_TUNNELING: 'GEARBOX_TUNNELING',
  // Switch Advanced Settings (Full Configurability): gates the Configuration > Advanced Settings
  // sub-tab + its schema-driven pages/routes. Backend-driven via featureConf (id
  // "SWITCH_ADVANCED_SETTINGS"); mirrored by FeatureConfigEnum.SWITCH_ADVANCED_SETTINGS in InsightCloud.
  SWITCH_ADVANCED_SETTINGS: 'SWITCH_ADVANCED_SETTINGS',
  // PR License Segmentation flag
  PR_LICENSE_SEGMENTATION: 'PR_LICENSE_SEGMENTATION',
  // PR IPv6 Phase 1 rollout flag. Backend-driven via featureConf (id "IPV6").
  IPV6: 'IPV6',
  // PR IPv6 Phase 2 (R07-R12) rollout flag. Open Question Q1 = A: Phase 2 reuses the Phase 1
  // featureConf id "IPV6", so this is the same value as IPV6. Kept as its own name so a later
  // separate flag is a one-line change. Mirrors FeatureConfigEnum.IPV6_PHASE2 in InsightCloud.
  IPV6_PHASE2: 'IPV6',
  /** Manager multi-admin invite / account picker. Backend-driven via featureConf. */
  MANAGER_MULTI_ADMIN_INVITE: 'MANAGER_MULTI_ADMIN_INVITE',
  // AIDN (AI Defined Network) — single featureConf gate. The former AIDN_UI_ENABLED / AIDN_API_ENABLED
  // flags were consolidated into this one (UI was FE-only; API now follows master).
  AIDN_MASTER_ENABLED: 'AIDN_MASTER_ENABLED',
  // Hindsight (telemetry investigation) — featureConf master gate. Backend mirrors this id
  // (HINDSIGHT_ENABLED) to 404 its endpoints when off; here it gates the Hindsight nav entries.
  HINDSIGHT_ENABLED: 'HINDSIGHT_ENABLED',
  SASE_LICENSING: 'SASE_LICENSING',
  SASE_LICENSE_EXPIRY: 'SASE_LICENSE_EXPIRY',
  CLIENT_TRIANGULATION: 'CLIENT_TRIANGULATION',
  // Client rename (PRJCBUGEN-66396): the Clients page's Rename action and dialog. Backend-driven via
  // featureConf (id "CLIENT_RENAME"); mirrored by FeatureConfigEnum.CLIENT_RENAME in InsightCloud,
  // which also refuses the rename API and leaves custom names out of the client list when off.
  CLIENT_RENAME: 'CLIENT_RENAME',
  // Security Center 2.0 SASE Users tab
  SECURITY_2_0_SASE_USERS: 'SECURITY_2_0_SASE_USERS'
};

const STORAGE_KEY = 'featureSupportAdminCheck';

let cachedRaw = null;
const cache = new Map();

function rebuildCache() {
  const raw = localStorage.getItem(STORAGE_KEY);

  // Skip if no change
  if (raw === cachedRaw) return;

  cachedRaw = raw;
  cache.clear();

  if (!raw) return;

  try {
    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) return;

    for (const entry of parsed) {
      const id = entry?.featureId;
      if (id == null) continue;

      cache.set(String(id), entry?.status === '1');
    }
  } catch {
    cache.clear();
  }
}

/**
 * @param {string | number | (string | number)[] | null | undefined} featureId - a single
 *   feature id, or an array of ids treated as an AND (all must be enabled). The array form
 *   backs multi-flag gates (the AIDN nav tab is now the single-id gate [AIDN_MASTER_ENABLED]).
 */
export function isFeatureEnabled(featureId) {
  if (featureId == null || featureId === '') return false;

  if (Array.isArray(featureId)) {
    // AND semantics: every id in the list must be enabled. An empty array gates nothing off
    // (returns false) so a mis-authored `featureGate: []` fails closed rather than showing.
    return featureId.length > 0 && featureId.every((id) => isFeatureEnabled(id));
  }

  rebuildCache();
  return cache.get(String(featureId)) === true;
}

/**
 * Opt-OUT variant of isFeatureEnabled: enabled unless the backend EXPLICITLY disables it
 * (feature-list entry present with status !== '1'). Absent from the list ⇒ enabled. Use for a
 * feature that ships ON by default and whose backend flag is only a kill-switch.
 * @param {string | number | null | undefined} featureId
 */
export function isFeatureEnabledByDefault(featureId) {
  if (featureId == null || featureId === '') return false;

  rebuildCache();
  // undefined (absent) or true ('1') ⇒ enabled; only an explicit `false` ('0') disables.
  return cache.get(String(featureId)) !== false;
}

export function invalidateSupportedFeatureCache() {
  cachedRaw = null;
  cache.clear();
  // Notify subscribers so components reading flags via useFeatureEnabled
  // re-render — needed because CloudRestApiAccessProvider populates the
  // localStorage cache asynchronously after mount.
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new Event(FEATURE_FLAGS_EVENT));
  }
}

const subscribeToFlagUpdates = (callback) => {
  // 'storage' fires for cross-tab updates; the custom event fires when the
  // current tab calls invalidateSupportedFeatureCache (set after the SPA
  // refreshes the feature list).
  const onStorage = (e) => {
    if (e.storageArea === localStorage && e.key === STORAGE_KEY) callback();
  };
  const onCustom = () => callback();
  window.addEventListener('storage', onStorage);
  window.addEventListener(FEATURE_FLAGS_EVENT, onCustom);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(FEATURE_FLAGS_EVENT, onCustom);
  };
};

/**
 * Reactive equivalent of isFeatureEnabled. Re-renders the component when the
 * feature list is refreshed (e.g. after GET_SUPPORTED_FEATURE_LIST resolves
 * on first load, or after an admin toggles a flag and the SPA re-fetches).
 *
 * @param {string | number | null | undefined} featureId
 * @returns {boolean}
 */
export function useFeatureEnabled(featureId) {
  return useSyncExternalStore(
    subscribeToFlagUpdates,
    () => isFeatureEnabled(featureId),
    () => false
  );
}

/**
 * Reactive equivalent of isFeatureEnabledByDefault (opt-out / default-ON): the component reads
 * `true` unless the backend explicitly disables the flag. Server/initial snapshot is `true` so the
 * feature is active before the feature list hydrates.
 *
 * @param {string | number | null | undefined} featureId
 * @returns {boolean}
 */
export function useFeatureEnabledByDefault(featureId) {
  return useSyncExternalStore(
    subscribeToFlagUpdates,
    () => isFeatureEnabledByDefault(featureId),
    () => true
  );
}

/**
 * True once CloudRestApiAccessProvider has populated the feature list for this
 * session — i.e. the localStorage key is present (even as `[]`). Lets callers
 * distinguish "flag is off" from "flags haven't loaded yet" so they can avoid
 * acting on a transient `false` (e.g. disabling tabs and triggering a redirect
 * before hydration completes).
 */
export function isFeatureListLoaded() {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEY) !== null;
}

export function useFeatureListLoaded() {
  return useSyncExternalStore(subscribeToFlagUpdates, isFeatureListLoaded, () => false);
}
